import { Calendar, Check, Repeat } from '@griply/assets';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateUserSettings } from 'features/user';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Sync16 } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuToggle,
} from 'shared/components/ui/popup-menu';
import { queryKey as externalCalendarEventsQueryKey } from 'shared/hooks/queries/use-external-calendar-events-query';
import { useUser } from 'shared/hooks/use-user';

export type MainContextMenuProps = {
  referenceElement?: React.RefObject<HTMLElement>;
  onClose: () => void;
};

export const MainContextMenu: React.FC<MainContextMenuProps> = ({
  referenceElement,
  onClose,
}) => {
  const { t } = useTranslation();
  const user = useUser();
  const queryClient = useQueryClient();

  const updateUserSettings = useUpdateUserSettings();
  const refreshCalendarEvents = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [externalCalendarEventsQueryKey],
    });
    onClose();
  }, [onClose, queryClient]);
  const toggleHideExternalCalendarEvents = () =>
    updateUserSettings({
      upcoming: {
        hideExternalCalendarEvents:
          !user?.settings?.upcoming?.hideExternalCalendarEvents,
      },
    });
  const toggleHideHabits = () =>
    updateUserSettings({
      upcoming: {
        hideHabits: !user?.settings?.upcoming?.hideHabits,
      },
    });
  const toggleHideCompleted = () =>
    updateUserSettings({
      upcoming: {
        hideCompleted: !user?.settings?.upcoming?.hideCompleted,
      },
    });

  return (
    <PopupMenu
      referenceElement={referenceElement}
      position="bottom-end"
      onOutside={onClose}
    >
      <PopupMenuList hasBottomBorder>
        <PopupMenuListItem>
          <PopupMenuToggle
            start={<Icon icon={Check} />}
            label={t('pages.upcoming.show-completed.label')}
            onChange={toggleHideCompleted}
            checked={!user?.settings?.upcoming?.hideCompleted}
          />
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuToggle
            start={<Icon icon={Repeat} />}
            label={t('pages.upcoming.show-habits.label')}
            onChange={toggleHideHabits}
            checked={!user?.settings?.upcoming?.hideHabits}
          />
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuToggle
            start={<Icon icon={Calendar} />}
            label={t('pages.upcoming.show-external-calendars.label')}
            onChange={toggleHideExternalCalendarEvents}
            checked={!user?.settings?.upcoming?.hideExternalCalendarEvents}
          />
        </PopupMenuListItem>
      </PopupMenuList>
      <PopupMenuList>
        <PopupMenuListItem>
          <PopupMenuButton
            start={<Icon icon={Sync16} />}
            onClick={refreshCalendarEvents}
          >
            {t('pages.today.side-panel.settings.sync-calendar.label')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>
    </PopupMenu>
  );
};
